import robinhoodOnchain from "./onchain/4663.json";
import { RH_CRYPTO, RH_STOCKS } from "./robinhood.generated";
import type { Address } from "./schemas";

export type AssetKind = "stock" | "stable" | "crypto";

export interface Asset {
  /** Unique key: the token's symbol (NVDA, WETH, USDG…). */
  symbol: string;
  name: string;
  kind: AssetKind;
  address: Address;
  decimals: number;
  /** Remote logo; the UI falls back to a monogram. */
  logo?: string;
  /** Ticker for display when it differs from the symbol (WETH shows as ETH). */
  ticker?: string;
  /** Issuer of a tokenized security. */
  issuer?: string | null;
  etf?: boolean;
  /** Deepest Uniswap pool seen when the catalog was generated, in USD (null: unknown). */
  liquidityUsd?: number | null;
  poolVersions?: ("v2" | "v3" | "v4")[];
  /**
   * Chainlink USD feed registered on the bips factory. Live vaults check every swap against it;
   * assets without one trade only when the owner opts in (`allowUnpriced`).
   */
  priceFeed?: { address: Address; maxAge: number };
}

/** Execution config shared with the Foundry deploy script (packages/shared/src/onchain/<id>.json). */
export interface OnchainConfig {
  chainId: number;
  permit2: Address;
  routers: { address: Address; name: string; usesPermit2: boolean }[];
  feeds: { feed: Address; maxAge: number; symbol: string; token: Address }[];
  /** Keyless fallback: Uniswap v3 routes through `hub`, used only without a Trading API key. */
  v3Fallback?: { quoter: Address; hub: Address; feeToHub: Record<string, number> };
}

export const onchainConfigs: Record<number, OnchainConfig> = {
  4663: robinhoodOnchain as OnchainConfig,
};

export interface ChainConfig {
  id: number;
  key: "robinhood";
  name: string;
  rpcUrl: string;
  explorer: string;
  /** The stable asset every vault prices against and the agent quotes into. */
  quoteAsset: string;
  permit2: Address;
  /** Universal Router 2.1.2; the Trading API stopped accepting 2.0 and 2.1.1 on 2026-10-21. */
  universalRouter: Address;
  /**
   * Universal Router 2.2.0, required by the Trading API when a token trades through permissioned
   * (issuer-gated) pools. Not deployed on Robinhood Chain, where Stock Tokens trade in ordinary pools.
   */
  permissionedRouter?: Address;
  poolManager: Address;
  /** Same-chain exact-input Guidestar routing (aggregator hooks) is available on this chain. */
  aggregatorHooks: boolean;
  assets: Asset[];
}

/** Attaches the factory's Chainlink feeds to the assets they price. */
function withFeeds(chainId: number, assets: Asset[]): Asset[] {
  const feeds = onchainConfigs[chainId]?.feeds ?? [];
  return assets.map((a) => {
    const f = feeds.find((x) => x.token.toLowerCase() === a.address.toLowerCase());
    return f ? { ...a, priceFeed: { address: f.feed, maxAge: f.maxAge } } : a;
  });
}

/** ETFs among the Robinhood Stock Tokens (the rest are single stocks). */
const ETFS = new Set(["SPY", "QQQ", "GLD", "SLV", "USO", "SGOV", "INDA", "SOXX", "VTI", "EWY"]);
const tokenName = (n: string) =>
  n
    .replace(/\s*•\s*Robinhood Tok.*$/, "")
    .replace(/,? Inc\.?$| Corporation$/i, "")
    .trim();

/**
 * Robinhood Chain mainnet: the only network bips runs on. Assets come from the on-chain catalogue
 * (robinhood.generated.ts: every Stock Token and established coin with a real Uniswap pool), priced by
 * the Chainlink feeds in onchain/4663.json. Every vault is funded in USDG.
 */
export const robinhoodChain: ChainConfig = {
  id: 4663,
  key: "robinhood",
  name: "Robinhood Chain",
  rpcUrl: "https://rpc.mainnet.chain.robinhood.com",
  explorer: "https://robinhoodchain.blockscout.com",
  quoteAsset: "USDG",
  permit2: robinhoodOnchain.permit2 as Address,
  universalRouter: robinhoodOnchain.routers[0]!.address as Address,
  poolManager: "0x8366a39cc670b4001a1121b8f6a443a643e40951",
  aggregatorHooks: true,
  assets: withFeeds(4663, [
    ...RH_CRYPTO.map(
      (c): Asset => ({
        // The catalogue lists wrapped ether as ETH; vaults hold the WETH token.
        symbol: c.symbol === "ETH" ? "WETH" : c.symbol,
        ticker: c.symbol === "ETH" ? "ETH" : undefined,
        name: c.symbol === "ETH" ? "Wrapped Ether" : tokenName(c.name),
        kind: c.kind,
        address: c.address,
        decimals: c.decimals,
        liquidityUsd: c.liquidityUsd,
        poolVersions: [c.bestPool.version],
      }),
    ),
    ...RH_STOCKS.map(
      (t): Asset => ({
        symbol: t.symbol,
        name: tokenName(t.name),
        kind: "stock",
        address: t.address,
        decimals: t.decimals,
        issuer: "Robinhood",
        etf: ETFS.has(t.symbol),
        liquidityUsd: t.liquidityUsd,
        poolVersions: [t.bestPool.version],
      }),
    ),
  ]),
};

export const chains = [robinhoodChain] as const;
export const defaultChain = robinhoodChain;

export function chainById(id: number): ChainConfig | undefined {
  return chains.find((c) => c.id === id);
}

export function assetOf(chain: ChainConfig, symbol: string): Asset | undefined {
  return chain.assets.find((a) => a.symbol === symbol);
}

export function explorerTx(chain: ChainConfig, hash: string) {
  return `${chain.explorer}/tx/${hash}`;
}

export function explorerAddress(chain: ChainConfig, address: string) {
  return `${chain.explorer}/address/${address}`;
}
